import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sliders, Save, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DEFAULTS = { P1: 75, P2: 180, P3: 360, P4: 720 };

export default function BaselineEditor({ onSaved }) {
  const [values, setValues] = useState(DEFAULTS);
  const [original, setOriginal] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api
      .getBaselines()
      .then((b) => {
        setValues(b);
        setOriginal(b);
      })
      .catch(() => {});
  }, []);

  const dirty =
    Object.keys(values).some((k) => Number(values[k]) !== Number(original[k]));

  const setField = (k, v) =>
    setValues((s) => ({ ...s, [k]: v === "" ? "" : Number(v) }));

  const save = async () => {
    setBusy(true);
    try {
      const cleaned = Object.fromEntries(
        Object.entries(values).map(([k, v]) => [k, Math.max(1, Number(v) || 0)])
      );
      const saved = await api.updateBaselines(cleaned);
      setValues(saved);
      setOriginal(saved);
      toast.success("Baselines saved — MTTR savings will recalculate");
      onSaved?.();
    } catch (e) {
      toast.error("Could not save baselines");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => setValues(DEFAULTS);

  return (
    <div className="cre-surface rounded-sm" data-testid="baseline-editor">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full p-5 flex items-center justify-between gap-3 text-left"
        data-testid="baseline-toggle"
      >
        <div className="flex items-center gap-2">
          <Sliders className="h-4 w-4 text-blue-300" strokeWidth={1.5} />
          <span className="font-heading text-base font-semibold tracking-tight">
            Manual AI workspace baselines
          </span>
          <span className="cre-overline text-zinc-500 ml-2">
            P1·{original.P1} P2·{original.P2} P3·{original.P3} P4·{original.P4} min
          </span>
        </div>
        <span className="text-[11px] font-mono text-zinc-500">
          {open ? "▾ collapse" : "▸ edit"}
        </span>
      </button>

      {open ? (
        <div className="p-5 pt-0 border-t border-white/5">
          <p className="text-xs text-zinc-400 leading-relaxed mb-4 max-w-2xl">
            Set the manual AI workspace MTTR baseline (in minutes) for each severity
            level. CRE compares actual triage + estimated execution to these
            baselines to compute time saved. Defaults are conservative industry
            estimates — adjust to match your organisation's historical MTTR.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {["P1", "P2", "P3", "P4"].map((sev) => (
              <div key={sev}>
                <Label className="cre-overline">{sev} baseline</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={values[sev]}
                    onChange={(e) => setField(sev, e.target.value)}
                    className="bg-black/40 border-white/10 font-mono"
                    data-testid={`baseline-${sev}`}
                  />
                  <span className="text-xs text-zinc-500 font-mono">min</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Button
              size="sm"
              disabled={busy || !dirty}
              onClick={save}
              data-testid="baseline-save"
              className="rounded-sm bg-blue-500 hover:bg-blue-400 text-white"
            >
              <Save className="h-3.5 w-3.5 mr-2" /> Save baselines
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={reset}
              data-testid="baseline-reset"
              className="rounded-sm border-white/15 text-zinc-300"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-2" /> Reset to defaults
            </Button>
            {dirty ? (
              <span className="text-[11px] font-mono text-amber-400 ml-2">
                · unsaved changes
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
